import { useMemo, useState } from "react";
import {
  Trash2,
  Search,
  Table as TableIcon,
  ArrowUpDown,
  ChevronDown,
  RefreshCw,
  Code2,
  Loader2,
} from "lucide-react";

import {
  ToolsViewConfiguration,
  type ToolsMode,
  type RuleFlags,
} from "./ToolsViewConfiguration";
import { JsonTree } from "./JsonTree";

/**
 * Line Item panel — tabular data view. Renders only what the backend
 * actually returned. Empty state is shown when no rows are present; we
 * never invent placeholder rows.
 *
 * Accepted shapes:
 *  1. `[{ col1, col2, ... }, ...]` — array of records.
 *  2. `{ headers: [...], rows: [[...], ...] }` — explicit columnar form.
 *  3. `{ tables: [{ name, rows: [...] }, ...] }` — multiple named tables.
 *  4. `{ status, message, data: <one of the above> }` — wrapped envelope.
 */

interface LineItemPanelProps {
  data?: unknown;
  layoutId?: string;
  isLoading?: boolean;
  onProcess?: () => void;
  /** Forwarded to the Process button so it spins while the parent mutation runs. */
  isProcessing?: boolean;
  onManualAdjustment?: () => void;
  onViewRules?: () => void;
  onDeleteTable?: (tableName: string, tableIndex: number) => void;
  onRetry?: () => void;
  mode?: ToolsMode;
  onModeChange?: (mode: ToolsMode) => void;
  ruleFlags?: RuleFlags;
  onRuleFlagsChange?: (next: RuleFlags) => void;
  /** Active cell highlight (drives the bbox overlay on the document). */
  activeCell?: { tableName: string; rowIndex: number; column: string } | null;
  onSelectCell?: (cell: {
    tableName: string;
    rowIndex: number;
    column: string;
    boundary: string;
    value: string;
  }) => void;
  /** Edit-in-place: parent owns the dirty state. */
  edits?: TableEdits;
  onEditCell?: (
    tableName: string,
    rowIndex: number,
    column: string,
    value: string,
  ) => void;
  selectedRow?: string;
  selectedColumn?: string;
  onRuleSelectionChange?: (selection: {
    selectedRow: string;
    selectedColumn: string;
  }) => void;
  /** Hide the top ToolsViewConfiguration block — used when the panel is
   *  rendered as the View Rules results pane and the configuration would
   *  duplicate the editor on the right. */
  hideConfiguration?: boolean;
  /**
   * Hide the per-row selection column (master checkbox in the header
   * + checkbox in each row) plus the toolbar Add/Delete buttons. Used
   * by the Final Checker, which is read-only — no row-level mutations
   * happen there, so the checkboxes were just visual noise.
   */
  hideRowSelection?: boolean;
  /** Locally-derived table (columns + OCR'd row values) used as a
   *  fallback when the projection hasn't returned tables yet. */
  tableFallback?: {
    columns: string[];
    rows: Record<string, string>[];
  } | null;
}

/** Edits keyed by `${tableName}:${rowIndex}:${column}`. */
export type TableEdits = Record<string, string>;

export function editKey(tableName: string, rowIndex: number, column: string): string {
  return `${tableName}${rowIndex}${column}`;
}

interface CellData {
  value: string;
  boundary: string;
}

interface TableData {
  name: string;
  columns: string[];
  /** Each row is a map of column → cell. Cells preserve `{value, boundary}`. */
  rows: Record<string, CellData>[];
}

function humanize(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

function asDisplay(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

/**
 * Eroyal cells come back as `{value, boundary}`. Normalize both into
 * a typed CellData. For plain scalars we synthesize an empty boundary.
 */
function toCell(raw: unknown): CellData {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if ("value" in obj || "boundary" in obj) {
      return {
        value: asDisplay(obj.value),
        boundary: typeof obj.boundary === "string" ? obj.boundary : "",
      };
    }
  }
  return { value: asDisplay(raw), boundary: "" };
}

function inferTables(data: unknown): TableData[] {
  if (!data) return [];

  // Unwrap { status, message, data } envelopes.
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if (obj.data !== undefined && !Array.isArray(obj.tables)) {
      return inferTables(obj.data);
    }

    // Eroyal shape: { tables: [{ table_name, table_rows, column_boundary, total_width }] }
    if (Array.isArray(obj.tables)) {
      const tables: TableData[] = [];
      (obj.tables as unknown[]).forEach((t, i) => {
        if (!t || typeof t !== "object") return;
        const tobj = t as Record<string, unknown>;

        const tableName =
          (typeof tobj.table_name === "string" && tobj.table_name) ||
          (typeof tobj.name === "string" && tobj.name) ||
          `Table ${i + 1}`;

        const rawRows =
          (tobj.table_rows as unknown[] | undefined) ??
          (tobj.rows as unknown[] | undefined) ??
          [];

        // Column ordering: prefer column_boundary keys (preserves authoring
        // order), fall back to first-row keys.
        let columns: string[] = [];
        if (tobj.column_boundary && typeof tobj.column_boundary === "object") {
          columns = Object.keys(tobj.column_boundary as Record<string, unknown>);
        } else if (Array.isArray(tobj.headers)) {
          columns = tobj.headers as string[];
        } else if (Array.isArray(tobj.columns)) {
          columns = tobj.columns as string[];
        } else if (rawRows.length > 0 && rawRows[0] && typeof rawRows[0] === "object") {
          columns = Object.keys(rawRows[0] as Record<string, unknown>);
        }

        // Each row: build typed CellData per column.
        const rows: Record<string, CellData>[] = [];
        for (const row of rawRows) {
          if (!row || typeof row !== "object") continue;
          if (Array.isArray(row)) {
            const flat: Record<string, CellData> = {};
            columns.forEach((c, idx) => {
              flat[c] = toCell((row as unknown[])[idx]);
            });
            rows.push(flat);
          } else {
            const flat: Record<string, CellData> = {};
            for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
              flat[k] = toCell(v);
            }
            rows.push(flat);
          }
        }

        tables.push({ name: String(tableName), columns, rows });
      });
      if (tables.length > 0) return tables;
    }

    // Explicit columnar form.
    if (Array.isArray(obj.headers) && Array.isArray(obj.rows)) {
      const headers = obj.headers as string[];
      const rows: Record<string, CellData>[] = (obj.rows as unknown[][]).map((arr) => {
        const r: Record<string, CellData> = {};
        headers.forEach((h, i) => (r[h] = toCell(arr[i])));
        return r;
      });
      return [{ name: "Table 1", columns: headers, rows }];
    }
  }

  // Bare array — the reference treats this two ways: a list of tables
  // (`extracted?.[0]?.layout_id`) when each item looks like a table
  // object, and a list of records otherwise. Distinguish by checking
  // for table_rows / rows on the items.
  if (Array.isArray(data) && data.length > 0) {
    const looksLikeArrayOfTables = data.some((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return false;
      const o = item as Record<string, unknown>;
      return Array.isArray(o.table_rows) || Array.isArray(o.rows);
    });
    if (looksLikeArrayOfTables) {
      // Recurse via the `{tables: [...]}` branch above.
      return inferTables({ tables: data });
    }
    if (data.every((item) => item && typeof item === "object" && !Array.isArray(item))) {
      const records: Record<string, CellData>[] = (data as Record<string, unknown>[]).map((r) => {
        const flat: Record<string, CellData> = {};
        for (const [k, v] of Object.entries(r)) flat[k] = toCell(v);
        return flat;
      });
      const colSet = new Set<string>();
      for (const r of records) Object.keys(r).forEach((k) => colSet.add(k));
      return [{ name: "Table 1", columns: Array.from(colSet), rows: records }];
    }
  }

  return [];
}

export function LineItemPanel({
  data,
  layoutId,
  isLoading,
  onProcess,
  isProcessing,
  onManualAdjustment,
  onViewRules,
  onDeleteTable,
  onRetry,
  mode,
  onModeChange,
  ruleFlags,
  onRuleFlagsChange,
  onSelectCell,
  edits,
  onEditCell,
  selectedRow,
  selectedColumn,
  onRuleSelectionChange,
  activeCell,
  hideConfiguration,
  hideRowSelection,
  tableFallback,
}: LineItemPanelProps) {
  // Show projection tables when they have rows; otherwise fall back to
  // a locally-derived table (built from select_coordinates + OCR words)
  // so the user sees the extracted values immediately even when
  // select_projection is empty or still being computed.
  const tables = useMemo(() => {
    const parsed = inferTables(data).filter((t) => t.rows.length > 0);
    if (parsed.length > 0) return parsed;
    if (
      tableFallback &&
      tableFallback.columns.length > 0 &&
      tableFallback.rows.length > 0
    ) {
      const rows: Record<string, CellData>[] = tableFallback.rows.map((r) => {
        const flat: Record<string, CellData> = {};
        for (const col of tableFallback.columns) {
          flat[col] = { value: r[col] ?? "", boundary: "" };
        }
        return flat;
      });
      return [{ name: "Table 1", columns: tableFallback.columns, rows }];
    }
    return [];
  }, [data, tableFallback]);
  const [activeTableIdx, setActiveTableIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const activeTable: TableData | null = tables[activeTableIdx] ?? null;

  const editsMap = edits ?? {};
  const cellDisplay = (
    tableName: string,
    rowIndex: number,
    column: string,
    cell: CellData,
  ): string => {
    const k = editKey(tableName, rowIndex, column);
    return k in editsMap ? editsMap[k] : cell.value;
  };

  // Sort/filter are based on the user-visible value (which may be an edit).
  type IndexedRow = { row: Record<string, CellData>; originalIndex: number };
  const sortedRows = useMemo<IndexedRow[]>(() => {
    if (!activeTable) return [];
    let rows: IndexedRow[] = activeTable.rows.map((row, originalIndex) => ({
      row,
      originalIndex,
    }));
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(({ row, originalIndex }) =>
        activeTable.columns
          .map((c) => cellDisplay(activeTable.name, originalIndex, c, row[c] ?? { value: "", boundary: "" }))
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    if (sortKey) {
      rows = [...rows].sort((a, b) => {
        const av = cellDisplay(activeTable.name, a.originalIndex, sortKey, a.row[sortKey] ?? { value: "", boundary: "" });
        const bv = cellDisplay(activeTable.name, b.originalIndex, sortKey, b.row[sortKey] ?? { value: "", boundary: "" });
        const cmp = av.localeCompare(bv, undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTable, search, sortKey, sortDir, editsMap]);

  const onSort = (col: string) => {
    if (sortKey === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col);
      setSortDir("asc");
    }
  };

  const toggleRow = (idx: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedRows((prev) =>
      prev.size === sortedRows.length ? new Set() : new Set(sortedRows.map((_, i) => i)),
    );
  };

  return (
    <div className="flex min-h-full flex-col bg-white">
      {hideConfiguration ? null : (
        <ToolsViewConfiguration
          type="lineitem"
          layoutId={layoutId}
          mode={mode}
          onModeChange={onModeChange}
          ruleFlags={ruleFlags}
          onRuleFlagsChange={onRuleFlagsChange}
          onProcess={onProcess}
          processing={isProcessing}
          onManualAdjustment={onManualAdjustment}
          onViewRules={onViewRules}
          selectedRow={selectedRow}
          selectedColumn={selectedColumn}
          onRuleSelectionChange={onRuleSelectionChange}
        />
      )}

      {/* Results header */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-gray-900">Results</h3>
        {tables.length > 0 ? (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-600">
            {tables.length} {tables.length === 1 ? "table" : "tables"}
          </span>
        ) : null}
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-sm font-medium text-gray-600">
            Loading results...
          </span>
          <SkeletonTable />
        </div>
      ) : tables.length === 0 ? (
        <EmptyState onRetry={onRetry} rawData={data} />
      ) : (
        <>
          {/* Tables list (only shown when more than one) */}
          {tables.length > 1 ? (
            <div className="flex flex-shrink-0 items-center gap-1 border-b border-gray-200 bg-gray-50/60 px-3 py-2">
              {tables.map((t, i) => (
                <button
                  key={`${t.name}-${i}`}
                  type="button"
                  onClick={() => {
                    setActiveTableIdx(i);
                    setSelectedRows(new Set());
                    setSortKey(null);
                  }}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    i === activeTableIdx
                      ? "bg-white text-blue-700 shadow-sm ring-1 ring-blue-100"
                      : "text-gray-600 hover:bg-white"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          ) : null}

          {activeTable ? (
            <>
              {/* Table title bar — mirrors eroyal "Table 1 · Delete Table" */}
              <header className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-2">
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <TableIcon className="h-4 w-4 text-blue-600" />
                  <strong className="font-semibold">{activeTable.name}</strong>
                  <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] tabular-nums text-gray-600">
                    {sortedRows.length} {sortedRows.length === 1 ? "row" : "rows"}
                  </span>
                </div>
                {onDeleteTable ? (
                  <button
                    type="button"
                    onClick={() => onDeleteTable(activeTable.name, activeTableIdx)}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete Table
                  </button>
                ) : null}
              </header>

              {/* Toolbar */}
              <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-1.5">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter rows…"
                    className="w-full rounded-md border border-gray-200 bg-gray-50 py-1 pl-7 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Table — no inner scroll; the entire right panel
                  scrolls together (see VerificationCockpitPage). */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead className="bg-gray-50/95">
                    <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-500">
                      {!hideRowSelection ? (
                        <th className="w-8 border-b border-gray-200 px-2 py-2">
                          <input
                            type="checkbox"
                            checked={
                              sortedRows.length > 0 &&
                              selectedRows.size === sortedRows.length
                            }
                            onChange={toggleAll}
                            className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                      ) : null}
                      {activeTable.columns.map((col) => (
                        <th
                          key={col}
                          onClick={() => onSort(col)}
                          className="cursor-pointer select-none border-b border-gray-200 px-3 py-2 hover:text-gray-700"
                        >
                          <span className="inline-flex items-center gap-1">
                            {humanize(col)}
                            {sortKey === col ? (
                              <ChevronDown
                                className={`h-3 w-3 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`}
                              />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 text-gray-300" />
                            )}
                          </span>
                        </th>
                      ))}
                      <th className="w-10 border-b border-gray-200 px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            activeTable.columns.length +
                            (hideRowSelection ? 1 : 2)
                          }
                          className="border-b border-gray-100 py-12 text-center text-xs text-gray-500"
                        >
                          No rows match your filter.
                        </td>
                      </tr>
                    ) : (
                      sortedRows.map(({ row, originalIndex }, displayIdx) => {
                        const isSelected = selectedRows.has(originalIndex);
                        return (
                          <tr
                            key={originalIndex}
                            className={`group border-b border-gray-100 ${
                              isSelected ? "bg-blue-50/60" : "hover:bg-gray-50"
                            }`}
                          >
                            {!hideRowSelection ? (
                              <td className="px-2 py-2 align-top">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleRow(originalIndex)}
                                  className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                              </td>
                            ) : null}
                            {activeTable.columns.map((col) => {
                              const cell = row[col] ?? { value: "", boundary: "" };
                              const k = editKey(activeTable.name, originalIndex, col);
                              const display =
                                k in editsMap ? editsMap[k] : cell.value;
                              const isEdited = k in editsMap && editsMap[k] !== cell.value;
                              const isActive =
                                activeCell?.tableName === activeTable.name &&
                                activeCell?.rowIndex === originalIndex &&
                                activeCell?.column === col;
                              return (
                                <td
                                  key={col}
                                  data-active-field={isActive ? "true" : undefined}
                                  className="px-3 py-1.5 align-top"
                                >
                                  <EditableCell
                                    value={display}
                                    isEdited={isEdited}
                                    isActive={isActive}
                                    hasBoundary={!!cell.boundary}
                                    onFocus={() =>
                                      onSelectCell?.({
                                        tableName: activeTable.name,
                                        rowIndex: originalIndex,
                                        column: col,
                                        boundary: cell.boundary,
                                        value: cell.value,
                                      })
                                    }
                                    onChange={(next) =>
                                      onEditCell?.(
                                        activeTable.name,
                                        originalIndex,
                                        col,
                                        next,
                                      )
                                    }
                                  />
                                </td>
                              );
                            })}
                            <td className="px-2 py-2 align-top text-[10px] text-gray-400 tabular-nums">
                              {displayIdx + 1}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function EmptyState({
  onRetry,
  rawData,
}: {
  onRetry?: () => void;
  rawData?: unknown;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const hasRawPayload =
    rawData !== undefined &&
    rawData !== null &&
    !(typeof rawData === "object" && Object.keys(rawData as object).length === 0);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <div className="rounded-full bg-gray-100 p-4">
          <TableIcon className="h-8 w-8 text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">No line items extracted yet</p>
          <p className="mt-1 max-w-xs text-xs text-gray-500">
            {hasRawPayload
              ? "The backend returned data but no recognizable tables were found. View the raw response below to inspect the shape."
              : "Run Process above to detect tables in this document, or wait for the backend to populate this section."}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          ) : null}
          {hasRawPayload ? (
            <button
              type="button"
              onClick={() => setShowRaw((s) => !s)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Code2 className="h-3.5 w-3.5" />
              {showRaw ? "Hide raw response" : "View raw response"}
            </button>
          ) : null}
        </div>
      </div>

      {showRaw && hasRawPayload ? (
        <div className="mx-4 mb-6 overflow-auto rounded-lg bg-gray-50 p-3 text-xs">
          <JsonTree value={rawData} initiallyExpanded depth={0} />
        </div>
      ) : null}
    </div>
  );
}

function EditableCell({
  value,
  isEdited,
  isActive,
  hasBoundary,
  onFocus,
  onChange,
}: {
  value: string;
  isEdited: boolean;
  isActive: boolean;
  hasBoundary: boolean;
  onFocus: () => void;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onClick={onFocus}
        title={value}
        className={`block w-full max-w-[14rem] truncate rounded border bg-transparent px-1.5 py-1 text-xs transition focus:outline-none ${
          isActive
            ? "border-blue-400 bg-white ring-2 ring-blue-100"
            : isEdited
            ? "border-amber-300 bg-amber-50 text-amber-900"
            : "border-transparent hover:border-gray-200 hover:bg-white"
        } ${value ? "text-gray-900" : "italic text-gray-400"}`}
      />
      {hasBoundary ? (
        <span
          className={`absolute right-1 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full ${
            isActive ? "bg-blue-500" : "bg-gray-300"
          }`}
          title="Has bounding-box data"
        />
      ) : null}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="w-full space-y-2 px-4 py-4">
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-3 rounded bg-gray-100" />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="grid grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map((_, j) => (
            <div key={j} className="h-7 rounded-md border border-gray-100 bg-gray-50" />
          ))}
        </div>
      ))}
    </div>
  );
}
